import {
  Company,
  Experience,
  ExperienceOption,
  ExperienceStatus,
  Paginator,
  Role,
  ScheduleSlotRow,
  ScheduleSummaryRow,
  Widget,
} from '@/types'

// DEV-370 Phase 5b
export type ExperienceResellerType = 'group' | 'external'

export type ExperienceReseller = {
  type: ExperienceResellerType
  company: Company
}
import { requests, responseBody } from '../http/axios'
import { removeNullParams, snakeCaseObject } from '@/helper/formatter'

export type ExperienceFilters = {
  categoryId?: string
  organizerId?: string | number
  limit?: number
  page?: number
  q?: string
  city?: string
  isIncluded?: boolean
  excludeInformationPage?: boolean
  status?: ExperienceStatus
  petAccess?: string
  tagIds?: string
  internalLabelIds?: string
  sortBy?: string
  minPrice?: number
  maxPrice?: number
  maxDistance?: number
  includeArchived?: boolean
  withTranslationStatus?: boolean | number
  missingTranslationsLocales?: string
  // DEV-370 FU: Book Now opt-in to include sister-chain Group experiences the
  // seller has picked into its catalog (no separate tab). External stays gated
  // behind its own permission/tab.
  includeGroup?: boolean
}

export type TranslationStatusSummary = {
  total: number
  incomplete: number
  supportedLocales: string[]
}

export type WidgetExperienceFilter = {
  categoryId?: string
  included?: boolean
  limit?: number
  page?: number
  q?: string
  widgetUuid: string
  organizerId?: string
  tagIds?: string
  internalLabelIds?: string
  isEditMode?: boolean
}

export const ExperienceService = {
  assignStaff: async (
    experience: Experience,
    staffIds: number[],
    role: Role
  ): Promise<void> =>
    await requests.put(`experiences/${experience.uuid}/assignStaff`, {
      staffIds,
      role,
    }),

  getAppWidgetExperiences: async (
    params: WidgetExperienceFilter
  ): Promise<Paginator<Experience>> =>
    await requests.get(
      `app/widgets/${params.widgetUuid}/experiences?included=1&` +
        removeNullParams(params).toString()
    ),

  getExternalExperiences: async (
    params: ExperienceFilters
  ): Promise<Paginator<Experience>> => {
    return await requests.get(
      `external_experiences?` + removeNullParams(params).toString()
    )
  },

  // DEV-370: Group Marketplace — sister-chain experiences available for resale
  getGroupExperiences: async (
    params: ExperienceFilters
  ): Promise<Paginator<Experience>> => {
    return await requests.get(
      `group_experiences?` + removeNullParams(params).toString()
    )
  },

  getExperiences: async (
    params: ExperienceFilters
  ): Promise<Paginator<Experience>> =>
    await requests.get(`experiences?` + removeNullParams(params).toString()),

  getWidgetExperiences: async (
    params: WidgetExperienceFilter
  ): Promise<Paginator<Experience>> =>
    await requests.get(
      `widgets/${params.widgetUuid}/experiences?` +
        removeNullParams(params).toString()
    ),

  getInternalExperiences: async (
    params: ExperienceFilters
  ): Promise<Paginator<Experience>> => {
    return await requests.get(
      `experiences?` + removeNullParams(params).toString()
    )
  },

  getTranslationStatusSummary: async (): Promise<TranslationStatusSummary> =>
    await requests
      .get('experiences/translation-status-summary')
      .then(responseBody),

  getAppExperience: async (uuid: string, locale: string): Promise<Experience> =>
    await requests
      .get(`app/experiences/${uuid}?locale=${locale}`)
      .then(responseBody),

  getExperience: async (
    uuid: string,
    isEditMode = false,
    locale?: string
  ): Promise<Experience> =>
    await requests
      .get(
        `experiences/${uuid}?withTranslationStatus=1` +
          (isEditMode ? '&isEditMode=1' : '') +
          (locale ? `&locale=${locale}` : '')
      )
      .then(responseBody),

  updateExperience: async (
    experience: Experience,
    locale?: string
  ): Promise<Experience> =>
    await requests
      .put(`experiences/${experience.uuid}?isEditMode=1`, {
        ...snakeCaseObject(experience),
        action: 'save_draft',
        ...(locale ? { locale } : {}),
      })
      .then(responseBody),

  finalizeExperience: async (
    experience: Experience,
    locale?: string
  ): Promise<Experience> =>
    await requests
      .put(`experiences/${experience.uuid}?isEditMode=1`, {
        ...snakeCaseObject(experience),
        action: 'finalize',
        ...(locale ? { locale } : {}),
      })
      .then(responseBody),

  createExperience: async (categoryId: number): Promise<Experience> =>
    await requests
      .post('experiences', { category_id: categoryId })
      .then(responseBody),

  // Status-only toggle (publish/pause). Sends just the target status so the
  // backend never rewrites translatable content (DEV-752).
  toggleExperienceStatus: async (
    experience: Experience,
    status: ExperienceStatus
  ): Promise<Experience> =>
    await requests
      .put(`experiences/${experience.uuid}/status`, { status })
      .then(responseBody),

  syncTags: async (experience: Experience, tagIds: number[]): Promise<void> =>
    await requests.put(`experiences/${experience.uuid}/tags`, {
      tagIds,
    }),

  syncInternalLabels: async (
    experience: Experience,
    internalLabelIds: number[]
  ): Promise<void> =>
    await requests.put(`experiences/${experience.uuid}/internal_labels`, {
      internalLabelIds,
    }),

  getAvailableOptions: async (
    experience: Experience,
    date: string,
    locale?: string,
    // DEV-730: operator "Book Now" / reschedule flows bypass the cutoff; public
    // pages omit this so the cutoff is enforced.
    bookNow?: boolean
  ): Promise<ExperienceOption[]> =>
    await requests
      .post(
        `experiences/${experience.uuid}/available_options/${date}${
          locale ? `?locale=${locale}` : ''
        }`,
        bookNow ? { book_now: true } : {}
      )
      .then(responseBody),

  updateExperienceFallbackLocale: async (
    uuid: string,
    locale: string
  ): Promise<void> =>
    await requests.put(`experiences/${uuid}/fallback_locale`, { locale }),

  visit: async (
    uuid: string,
    guestId: string,
    isMobile: boolean
  ): Promise<void> =>
    await requests
      .post(`app/experiences/${uuid}/visit`, { guestId, isMobile })
      .then(responseBody),

  getCities: async (): Promise<string[]> => await requests.get(`cities`),

  removeExternalExperience: async (
    companyId: number,
    experienceId: number
  ): Promise<void> =>
    await requests.delete(`external_experiences/${companyId}/${experienceId}`),

  addExternalExperience: async (
    companyId: number,
    experienceId: number
  ): Promise<void> =>
    await requests.post(
      `external_experiences/${companyId}/${experienceId}`,
      {}
    ),

  removeGroupExperience: async (
    companyId: number,
    experienceId: number
  ): Promise<void> =>
    await requests.delete(`group_experiences/${companyId}/${experienceId}`),

  addGroupExperience: async (
    companyId: number,
    experienceId: number
  ): Promise<void> =>
    await requests.post(`group_experiences/${companyId}/${experienceId}`, {}),

  // DEV-370 Phase 5b: locations that resell this experience (Group + External)
  getExperienceResellers: async (
    uuid: string
  ): Promise<{ data: ExperienceReseller[] }> =>
    await requests.get(`experiences/${uuid}/resellers`),

  addWidgetExperience: async (
    widgetUuid: string,
    experienceId: number
  ): Promise<void> =>
    await requests.post(`widgets/${widgetUuid}/experiences`, { experienceId }),

  removeWidgetExperience: async (
    widgetUuid: string,
    experienceId: number
  ): Promise<void> =>
    await requests.delete(`widgets/${widgetUuid}/experiences/${experienceId}`),

  reorderWidgetExperiences: async (
    widgetUuid: string,
    experienceIds: number[]
  ): Promise<void> =>
    await requests.put(`widgets/${widgetUuid}/experiences/reorder`, {
      experienceIds,
    }),

  getExperienceWidgets: async (
    experienceUuid: string
  ): Promise<{ data: Widget[] }> =>
    await requests.get(`experiences/${experienceUuid}/widgets`),

  deleteExperience: async (experienceId: number): Promise<void> =>
    await requests.delete(`experiences/${experienceId}`),

  async getAvailableDates(
    experienceId: string,
    startDate: string,
    locale?: string,
    // DEV-730: operator "Book Now" / reschedule flows bypass the cutoff; public
    // pages omit this so the cutoff is enforced.
    bookNow?: boolean
  ): Promise<string[]> {
    const params = new URLSearchParams()
    if (locale) params.set('locale', locale)
    if (bookNow) params.set('book_now', '1')
    const query = params.toString()

    return await requests.get(
      `/experiences/${experienceId}/open_dates/${startDate}${
        query ? `?${query}` : ''
      }`
    )
  },

  archiveExperience: async (uuid: string): Promise<Experience> =>
    await requests.put(`experiences/${uuid}/archive`, {}).then(responseBody),

  unarchiveExperience: async (uuid: string): Promise<Experience> =>
    await requests.put(`experiences/${uuid}/unarchive`, {}).then(responseBody),

  duplicateExperience: async (uuid: string): Promise<Experience> =>
    await requests.post(`experiences/${uuid}/duplicate`, {}).then(responseBody),

  /**
   * DEV-687 - one row per (option × date) for the schedule month view.
   */
  getScheduleSummary: async (args: {
    dateFrom: string
    dateTo: string
    chainId?: string
    locationIds?: string
    experienceIds?: string
    categoryIds?: string
    tagIds?: string
    labelIds?: string
  }): Promise<ScheduleSummaryRow[]> => {
    const params = new URLSearchParams({
      date_from: args.dateFrom,
      date_to: args.dateTo,
    })
    if (args.chainId) params.append('chainId', args.chainId)
    if (args.locationIds) params.append('locationIds', args.locationIds)
    if (args.experienceIds) params.append('experienceIds', args.experienceIds)
    if (args.categoryIds) params.append('categoryIds', args.categoryIds)
    if (args.tagIds) params.append('tagIds', args.tagIds)
    if (args.labelIds) params.append('labelIds', args.labelIds)
    const raw = await requests
      .get(`experiences/schedule/summary?${params.toString()}`)
      .then(responseBody)
    const list = Array.isArray(raw) ? raw : raw?.data
    return Array.isArray(list) ? list : []
  },

  /**
   * DEV-687 - per-slot rows for a single (option × date), consumed by the
   * drilldown drawer opened on click from the summary cell.
   */
  getScheduleDetail: async (args: {
    optionId: number
    date: string
  }): Promise<ScheduleSlotRow[]> => {
    const params = new URLSearchParams({
      option_id: String(args.optionId),
      date: args.date,
    })
    const raw = await requests
      .get(`experiences/schedule/detail?${params.toString()}`)
      .then(responseBody)
    const list = Array.isArray(raw) ? raw : raw?.data
    return Array.isArray(list) ? list : []
  },

  bulkCreateExceptions: async (args: {
    experienceIds: number[]
    startDate: string
    endDate?: string | null
    reason?: string | null
  }): Promise<{ created: number }> => {
    return await requests.post('experiences/bulk-exceptions', {
      experience_ids: args.experienceIds,
      start_date: args.startDate,
      end_date: args.endDate ?? null,
      reason: args.reason ?? null,
    })
  },
}
